import { Head, useForm } from "@inertiajs/react";
import { t } from "@travily/i18n";
import { serviceEditorPath, serviceEditorSectionPath, servicePath } from "@travily/routes";
import type ListingsEditorDescription from "@travily/types/pages/ListingsEditorDescription";
import { ButtonDock } from "@ui/components/ButtonDock";
import { StandardButton } from "@ui/components/StandardButton";
import type { ReactNode } from "react";
import { EditFrame } from "@/layouts/edit-frame";
import PersistentLayout from "@/layouts/persistent-layout";
import { buildEditorSections } from "@/lib/editor-sections";
import { firstError } from "@/lib/forms/first-error";
import { ServiceDescriptionEditor } from "./show.view";

type PageProps = ListingsEditorDescription;

interface DescriptionFormData {
  description: string;
}

const ServiceEditDescriptionPage = (props: PageProps) => {
  const { listing_id, description } = props;
  const sections = buildEditorSections(props);
  const form = useForm<DescriptionFormData>({ description: description ?? "" });

  const handleSave = () => {
    form.patch(serviceEditorSectionPath(listing_id, "description"), {
      preserveScroll: "errors",
    });
  };

  return (
    <EditFrame
      activeSection="description"
      actions={
        <ButtonDock
          primaryAction={
            <>
              <StandardButton
                className="sm:hidden"
                color="green"
                isDisabled={!form.isDirty || form.processing}
                onPress={handleSave}
                size="56"
              >
                {t("common.actions.save")}
              </StandardButton>
              <StandardButton
                className="hidden sm:flex"
                color="green"
                isDisabled={!form.isDirty || form.processing}
                onPress={handleSave}
                size="64"
              >
                {t("common.actions.save")}
              </StandardButton>
            </>
          }
        />
      }
      backHref={servicePath(listing_id)}
      href={serviceEditorPath(listing_id)}
      sections={sections}
      title={t("web.services.editors.show.title")}
    >
      <Head title={t("web.services.editor.sections.show.meta_title", { title: props.listing_title ?? "" })} />
      <ServiceDescriptionEditor
        error={firstError(form.errors.description as string | string[] | undefined)}
        onChange={(value) => form.setData("description", value)}
        value={form.data.description}
      />
    </EditFrame>
  );
};

ServiceEditDescriptionPage.layout = (page: ReactNode) => <PersistentLayout>{page}</PersistentLayout>;

export default ServiceEditDescriptionPage;
